import { useEffect, useRef, useState } from "react";
import timestampService from "../../service/timestampService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import labels from "../../utils/labels";
import TimePicker from "../UI/input/TimePicker";
import LabeledSection from "../UI/LabeledSection";
import EmployeeSettingsService from "../../service/employeeSettingsService";
import {
  clearEmployeeTransportEdit,
  clearEmployeeWorkTimeEdit,
  editEmployeeTransportType,
  editEmployeeWorkTime,
} from "../../store/employeeSettings";
import StandardButton from "../UI/buttons/StandardButton";
import EmployeeSettingsStyles from "../../styles/employeeSettings.module.css";
import { useQueryParams } from "../../hooks/useQueryParams";
import { TransportationType } from "../../types/ganttChart";

type LocalEmployeeWorkTime = {
  workTimeStart: string;
  workTimeEnd: string;
};
const localDefaultTime: LocalEmployeeWorkTime = {
  workTimeStart: "00:00",
  workTimeEnd: "00:00",
};

const EmployeeWorktime = ({
  onUnsavedEdit,
  onDeleteWorkTimeRequest,
}: {
  onUnsavedEdit: (hasChanges: boolean) => void;
  onDeleteWorkTimeRequest: () => void;
}) => {
  const { employeeWorkTime, employeeShiftId, manualShiftChanges } =
    useAppSelector((state) => state.drawerSlice.selectedEmployee!);
  const { manualEdits } = useAppSelector(
    (state) => state.employeeSettingsSlice,
  );
  const dispatch = useAppDispatch();
  const [localWorkTime, setLocalWorkTime] =
    useState<LocalEmployeeWorkTime>(localDefaultTime);
  const initialWorkTimeRef = useRef<LocalEmployeeWorkTime>(localDefaultTime);
  const [isPending, setIsPending] = useState(false);
  const [isAddingWorkTime, setIsAddingWorkTime] = useState(false);
  const { date } = useQueryParams();

  const manualWorkTimeEdits =
    manualEdits[employeeShiftId]?.employeeWorkTime ??
    manualShiftChanges?.employeeWorkTime;
  const timeToUse = manualWorkTimeEdits ?? employeeWorkTime;

  const isMissingWorkTime =
    !timeToUse ||
    (timeToUse.workTimeStart == null && timeToUse.workTimeEnd == null);

  useEffect(() => {
    if (isMissingWorkTime && !isAddingWorkTime) return;
    if (isMissingWorkTime || isAddingWorkTime) {
      setLocalWorkTime(localDefaultTime);
      initialWorkTimeRef.current = localDefaultTime;
    } else {
      const mappedTime = {
        workTimeStart: timestampService.getHoursAndMinutesTimestamp(
          timeToUse.workTimeStart!,
        ),
        workTimeEnd: timestampService.getHoursAndMinutesTimestamp(
          timeToUse.workTimeEnd!,
        ),
      };
      setLocalWorkTime(mappedTime);
      initialWorkTimeRef.current = mappedTime;
    }
  }, [employeeShiftId, timeToUse, isAddingWorkTime, isMissingWorkTime]);

  function onInputChange(time: "start" | "end", value: string) {
    const newTimeWindow = {
      workTimeStart: time === "start" ? value : localWorkTime.workTimeStart,
      workTimeEnd: time === "end" ? value : localWorkTime.workTimeEnd,
    };
    setLocalWorkTime(newTimeWindow);
  }

  const hasChanges =
    localWorkTime.workTimeStart !== initialWorkTimeRef.current.workTimeStart ||
    localWorkTime.workTimeEnd !== initialWorkTimeRef.current.workTimeEnd;

  useEffect(() => {
    onUnsavedEdit(hasChanges);
  }, [onUnsavedEdit, hasChanges]);

  const addWorkTime = () => {
    setIsAddingWorkTime(true);
    setLocalWorkTime(localDefaultTime);
    initialWorkTimeRef.current = localDefaultTime;
  };

  async function handleTimeWindowUpdate() {
    const baseDateForMerge = isMissingWorkTime ? date : timeToUse.workTimeStart;
    if (!baseDateForMerge) return;
    const updatedStart = timestampService.mergeTimeIntoDate(
      baseDateForMerge,
      localWorkTime.workTimeStart,
    );
    const updatedEnd = timestampService.mergeTimeIntoDate(
      baseDateForMerge,
      localWorkTime.workTimeEnd,
    );
    const matchesBaseWorkTime =
      updatedStart === employeeWorkTime.workTimeStart &&
      updatedEnd === employeeWorkTime.workTimeEnd;
    setIsPending(true);

    try {
      await EmployeeSettingsService.editEmployeeWorkTime(
        employeeShiftId,
        updatedStart,
        updatedEnd,
        dispatch,
      );
      dispatch(
        editEmployeeWorkTime({
          shiftId: employeeShiftId,
          workTimeStart: updatedStart,
          workTimeEnd: updatedEnd,
        }),
      );
      // if the edit is back to original worktime - clear that from the store
      if (matchesBaseWorkTime)
        dispatch(clearEmployeeWorkTimeEdit({ shiftId: employeeShiftId }));
      if (isMissingWorkTime) {
        const defaultTransportType: TransportationType = "CAR";
        await EmployeeSettingsService.editEmployeeTransportType(
          employeeShiftId,
          defaultTransportType,
          dispatch,
        );
        dispatch(
          editEmployeeTransportType({
            shiftId: employeeShiftId,
            transportationType: defaultTransportType,
          }),
        );
        dispatch(clearEmployeeTransportEdit({ shiftId: employeeShiftId }));
      }
      initialWorkTimeRef.current = localWorkTime;
      setIsAddingWorkTime(false);
      setIsPending(false);
    } catch {
      setLocalWorkTime(initialWorkTimeRef.current);
      setIsPending(false);
    }
  }

  const handleDiscardWorkTimeChanges = () => {
    setIsAddingWorkTime(false);
    setLocalWorkTime(
      isMissingWorkTime ? localDefaultTime : initialWorkTimeRef.current,
    );
  };

  const isDraftIncomplete =
    localWorkTime.workTimeStart === "00:00" ||
    localWorkTime.workTimeEnd === "00:00";
  const showDelete = !isMissingWorkTime || isAddingWorkTime;

  return (
    <LabeledSection
      title={labels.editWorkingHours}
      titleSize="h4"
      spacing="small"
      extraOptions={
        <StandardButton
          ariaLabel={showDelete ? labels.deleteWorkTime : labels.addWorkTime}
          variant="TERTIARY"
          onClick={showDelete ? onDeleteWorkTimeRequest : addWorkTime}
          classes="!h-[17.5px]"
          disable={isMissingWorkTime && isAddingWorkTime}
        >
          {showDelete ? labels.deleteWorkTime : labels.addWorkTime}
        </StandardButton>
      }
    >
      {isMissingWorkTime && !isAddingWorkTime ? (
        <div className="flex flex-col gap-[var(--spacing-sm)] text-[var(--col-text-secondary)]">
          {labels.noWorkTime}
        </div>
      ) : (
        <div className="flex flex-col w-full gap-[var(--spacing-md)]">
          <div
            className={`flex gap-[var(--spacing-md)]
          ${manualWorkTimeEdits && EmployeeSettingsStyles.editedSetting}`}
          >
            <TimePicker
              id="employee-start-time"
              value={localWorkTime.workTimeStart}
              onTimeChange={(newStart) => {
                onInputChange("start", newStart);
              }}
              label={labels.startTime}
              classes="w-full"
              helperText={manualWorkTimeEdits && labels.manuallyEditedWorkTime}
            />
            <TimePicker
              id="employee-end-time"
              value={localWorkTime.workTimeEnd}
              onTimeChange={(newEnd) => {
                onInputChange("end", newEnd);
              }}
              label={labels.endTime}
              classes="w-full"
              helperText={manualWorkTimeEdits && labels.manuallyEditedWorkTime}
            />
          </div>
          {(isAddingWorkTime || hasChanges || isPending) && (
            <div className="flex gap-[var(--spacing-sm)] w-full justify-end">
              <StandardButton
                ariaLabel={labels.discard}
                variant="SECONDARY"
                classes="w-fit"
                onClick={handleDiscardWorkTimeChanges}
              >
                {labels.discard}
              </StandardButton>

              <StandardButton
                ariaLabel={labels.save}
                variant="PRIMARY"
                classes="w-[84px]"
                onClick={handleTimeWindowUpdate}
                pending={isPending}
                disable={isAddingWorkTime && isDraftIncomplete}
              >
                {labels.save}
              </StandardButton>
            </div>
          )}
        </div>
      )}
    </LabeledSection>
  );
};

export default EmployeeWorktime;
